import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateSeries } from '@/api/series'
import { seriesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { SeriesResponseDto } from '@/api/generated/api'

interface UpdateSeriesData {
  name?: string
  description?: string
}

/**
 * Hook to update an existing series
 *
 * Handles:
 * - Optimistic update: immediately updates cache before API call
 * - API call to update series
 * - Cache invalidation on success
 * - Rollback on error
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const updateSeries = useUpdateSeries()
 *
 * const handleSave = async () => {
 *   await updateSeries.mutateAsync({
 *     seriesId: 'series-123',
 *     data: { name: 'Updated Name', description: 'New description' }
 *   })
 * }
 * ```
 */
export function useUpdateSeries() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      seriesId,
      data,
    }: {
      seriesId: string
      data: UpdateSeriesData
    }) => updateSeries(seriesId, data),
    onMutate: async ({
      seriesId,
      data,
    }: {
      seriesId: string
      data: UpdateSeriesData
    }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: seriesKeys.detail(seriesId) })

      // Snapshot previous value
      const previousSeries = queryClient.getQueryData(
        seriesKeys.detail(seriesId)
      ) as SeriesResponseDto | undefined

      // Optimistically update cache
      queryClient.setQueryData(
        seriesKeys.detail(seriesId),
        (old: SeriesResponseDto | undefined) => ({
          ...old,
          ...data,
          updatedAt: new Date().toISOString(),
        })
      )

      // Return context with previous value
      return { previousSeries, seriesId }
    },
    onSuccess: (
      updatedSeries: SeriesResponseDto,
      variables: { seriesId: string; data: UpdateSeriesData }
    ) => {
      // Invalidate detail and list queries
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(variables.seriesId),
      })
      queryClient.invalidateQueries({ queryKey: seriesKeys.lists() })

      // Show success toast
      toast({
        title: 'Series updated',
        description: `"${updatedSeries.name}" has been updated successfully.`,
      })
    },
    onError: (
      error: Error,
      _variables: { seriesId: string; data: UpdateSeriesData },
      context:
        | { previousSeries?: SeriesResponseDto; seriesId: string }
        | undefined
    ) => {
      // Rollback optimistic update
      if (context?.previousSeries) {
        queryClient.setQueryData(
          seriesKeys.detail(context.seriesId),
          context.previousSeries
        )
      }

      // Show error toast
      toast({
        title: 'Failed to update series',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
